// A projective image is sampled once per output pixel, never composited as
// independently antialiased triangles. WebGL is fast during corner dragging;
// the software path supports browsers with WebGL unavailable or context loss.
const rasters = new WeakMap();
let gpu;

function inverseMatrix(h) {
  const [a, b, c, d, e, f, g, j] = h;
  const inverse = [e - f * j, c * j - b, b * f - c * e,
    f * g - d, a - c * g, c * d - a * f,
    d * j - e * g, b * g - a * j, a * e - b * d];
  const determinant = a * inverse[0] + b * inverse[3] + c * inverse[6];
  return inverse.map(value => value / determinant);
}

function rasterizePixels(bytes, sw, sh, inverse, rect, region, bounds, width, height) {
  const output = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const u = (bounds.x + (x + 0.5) * bounds.width / width) / bounds.logicalWidth;
    const v = (bounds.y + (y + 0.5) * bounds.height / height) / bounds.logicalHeight;
    const denominator = inverse[6] * u + inverse[7] * v + inverse[8];
    const sx = rect.x + (inverse[0] * u + inverse[1] * v + inverse[2]) / denominator * rect.width;
    const sy = rect.y + (inverse[3] * u + inverse[4] * v + inverse[5]) / denominator * rect.height;
    if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx < region.x || sy < region.y
      || sx > region.x + region.width || sy > region.y + region.height) continue;
    const px = sx - 0.5, py = sy - 0.5;
    const left = Math.floor(px), top = Math.floor(py);
    const dx = px - left, dy = py - top;
    let alpha = 0, red = 0, green = 0, blue = 0;
    for (let row = 0; row < 2; row++) for (let column = 0; column < 2; column++) {
      const ix = Math.max(0, Math.min(sw - 1, left + column));
      const iy = Math.max(0, Math.min(sh - 1, top + row));
      const index = (iy * sw + ix) * 4;
      const weight = (column ? dx : 1 - dx) * (row ? dy : 1 - dy);
      // Interpolate premultiplied colour so transparent pixels/erased holes
      // cannot introduce coloured or dark fringes.
      const coverage = bytes[index + 3] * weight;
      alpha += coverage;
      red += bytes[index] * coverage; green += bytes[index + 1] * coverage;
      blue += bytes[index + 2] * coverage;
    }
    const index = (y * width + x) * 4;
    if (alpha > 0) {
      output[index] = red / alpha; output[index + 1] = green / alpha;
      output[index + 2] = blue / alpha; output[index + 3] = alpha;
    }
  }
  return output;
}

function createGpu() {
  const canvas = document.createElement('canvas');
  canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    gpu = null;
  });
  canvas.addEventListener('webglcontextrestored', () => { gpu = undefined; });
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true,
    preserveDrawingBuffer: true, antialias: false });
  if (!gl) return null;
  const program = gl.createProgram();
  const shaders = [];
  try {
    for (const [type, source] of [
      [gl.VERTEX_SHADER, 'attribute vec2 position; varying vec2 uv; void main(){uv=(position+1.0)*0.5;gl_Position=vec4(position,0.0,1.0);}'],
      [gl.FRAGMENT_SHADER, `
        precision highp float;
        varying vec2 uv;
        uniform sampler2D image;
        uniform mat3 inverse;
        uniform vec4 bounds;
        uniform vec2 logicalSize;
        uniform vec4 sourceRect;
        uniform vec4 region;
        uniform vec2 sourceSize;
        void main(){
          vec2 p=(bounds.xy+vec2(uv.x,1.0-uv.y)*bounds.zw)/logicalSize;
          vec3 s=inverse*vec3(p,1.0);
          vec2 pixel=sourceRect.xy+(s.xy/s.z)*sourceRect.zw;
          if(pixel.x<region.x||pixel.y<region.y||pixel.x>region.x+region.z||pixel.y>region.y+region.w){
            gl_FragColor=vec4(0.0);
          }else{
            gl_FragColor=texture2D(image,pixel/sourceSize);
          }
        }`],
    ]) {
      const shader = gl.createShader(type); shaders.push(shader);
      gl.shaderSource(shader, source); gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
      gl.attachShader(program, shader);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return { canvas, gl, program, buffer, texture };
  } catch (error) {
    console.warn('Perspective GPU renderer unavailable; using software rendering.', error);
    gl.deleteProgram(program);
    return null;
  } finally {
    shaders.forEach(shader => gl.deleteShader(shader));
  }
}

function gpuRender(image, inverse, rect, region, bounds, sw, sh, width, height) {
  if (gpu === undefined) gpu = createGpu();
  if (!gpu || gpu.gl.isContextLost() || sw > gpu.gl.getParameter(gpu.gl.MAX_TEXTURE_SIZE)
    || sh > gpu.gl.getParameter(gpu.gl.MAX_TEXTURE_SIZE)) return null;
  const { canvas, gl, program, buffer, texture } = gpu;
  canvas.width = width; canvas.height = height;
  gl.viewport(0, 0, width, height);
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  const position = gl.getAttribLocation(program, 'position');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.uniform1i(gl.getUniformLocation(program, 'image'), 0);
  gl.uniformMatrix3fv(gl.getUniformLocation(program, 'inverse'), false,
    new Float32Array([inverse[0], inverse[3], inverse[6], inverse[1], inverse[4], inverse[7], inverse[2], inverse[5], inverse[8]]));
  gl.uniform4f(gl.getUniformLocation(program, 'bounds'), bounds.x, bounds.y, bounds.width, bounds.height);
  gl.uniform2f(gl.getUniformLocation(program, 'logicalSize'), bounds.logicalWidth, bounds.logicalHeight);
  gl.uniform4f(gl.getUniformLocation(program, 'sourceRect'), rect.x, rect.y, rect.width, rect.height);
  gl.uniform4f(gl.getUniformLocation(program, 'region'), region.x, region.y, region.width, region.height);
  gl.uniform2f(gl.getUniformLocation(program, 'sourceSize'), sw, sh);
  gl.disable(gl.BLEND);
  gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  return gl.isContextLost() ? null : canvas;
}

function renderProjectiveRaster(ctx, image, sw, sh, h, rect, region, outer, logicalWidth, logicalHeight, revision) {
  const x = Math.min(...outer.map(point => point.x)), y = Math.min(...outer.map(point => point.y));
  const width = Math.max(...outer.map(point => point.x)) - x;
  const height = Math.max(...outer.map(point => point.y)) - y;
  const transform = ctx.getTransform?.();
  const density = transform ? Math.max(1, Math.hypot(transform.a, transform.b), Math.hypot(transform.c, transform.d)) : 1;
  const ratio = Math.min(density, Math.sqrt(8000000 / (width * height)), 4096 / width, 4096 / height);
  const rw = Math.max(1, Math.floor(width * ratio)), rh = Math.max(1, Math.floor(height * ratio));
  const bounds = { x, y, width, height, logicalWidth, logicalHeight };
  const key = revision == null ? null : JSON.stringify([sw, sh, h, rect, region, rw, rh, revision, logicalWidth, logicalHeight]);
  const cached = key && rasters.get(image);
  if (cached && cached.key === key) return cached.result;
  const inverse = inverseMatrix(h);
  const canvas = document.createElement('canvas'); canvas.width = rw; canvas.height = rh;
  const output = canvas.getContext('2d');
  const rendered = gpuRender(image, inverse, rect, region, bounds, sw, sh, rw, rh);
  if (rendered) {
    output.drawImage(rendered, 0, 0);
  } else {
    const source = document.createElement('canvas'); source.width = sw; source.height = sh;
    const input = source.getContext('2d', { willReadFrequently: true });
    input.drawImage(image, 0, 0, sw, sh);
    const pixels = rasterizePixels(input.getImageData(0, 0, sw, sh).data,
      sw, sh, inverse, rect, region, bounds, rw, rh);
    const frame = output.createImageData(rw, rh); frame.data.set(pixels);
    output.putImageData(frame, 0, 0);
  }
  const result = { canvas, ...bounds };
  if (key) rasters.set(image, { key, result });
  return result;
}

module.exports = { renderProjectiveRaster, rasterizePixels, inverseMatrix };
